import { type MessageDescriptor } from 'react-intl';

import { type RovoResponseTone } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { personalityGreetingMessages } from './messages';

/**
 * The three copy buckets from content design: `thinkDeeper` for the think-deeper
 * family, `taskMode` for task / work, and `normal` for everything else. Deep
 * research uses normal copy while retaining its dedicated presentation.
 */
export type GreetingMode = 'normal' | 'thinkDeeper' | 'taskMode';

const GREETING_BUCKET_BY_PREFIX = {
	collaborativeNormal: ['normal', 'rovo_default'],
	professionalNormal: ['normal', 'professional'],
	creativeNormal: ['normal', 'creative'],
	empatheticNormal: ['normal', 'empathetic'],
	collaborativeThinkDeeper: ['thinkDeeper', 'rovo_default'],
	professionalThinkDeeper: ['thinkDeeper', 'professional'],
	creativeThinkDeeper: ['thinkDeeper', 'creative'],
	empatheticThinkDeeper: ['thinkDeeper', 'empathetic'],
	collaborativeTask: ['taskMode', 'rovo_default'],
	professionalTask: ['taskMode', 'professional'],
	creativeTask: ['taskMode', 'creative'],
	empatheticTask: ['taskMode', 'empathetic'],
} as const satisfies Record<string, readonly [GreetingMode, RovoResponseTone]>;

/**
 * Curated greeting sets keyed by (mode, tone). Selection shuffles within the set
 * for the active pair; see `greetingRotation`.
 */
const greetingSets: Record<GreetingMode, Record<RovoResponseTone, MessageDescriptor[]>> = {
	normal: {
		rovo_default: [],
		professional: [],
		creative: [],
		empathetic: [],
	},
	thinkDeeper: {
		rovo_default: [],
		professional: [],
		creative: [],
		empathetic: [],
	},
	taskMode: {
		rovo_default: [],
		professional: [],
		creative: [],
		empathetic: [],
	},
};

for (const [key, descriptor] of Object.entries(personalityGreetingMessages)) {
	const prefix = key.replace(/\d+$/, '') as keyof typeof GREETING_BUCKET_BY_PREFIX;
	const bucket = GREETING_BUCKET_BY_PREFIX[prefix];
	if (!bucket) {
		throw new Error(`Unknown personality greeting prefix: ${prefix}`);
	}
	const [mode, tone] = bucket;
	greetingSets[mode][tone].push(descriptor);
}

export const GREETING_SETS: Record<
	GreetingMode,
	Record<RovoResponseTone, MessageDescriptor[]>
> = greetingSets;
